import React from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom';
import { useAuthContext } from './components/hooks/useAuthContext';

import Navbar from './components/Navbar';
import MobileNav from './components/MobileNav';

import HomeScreen from './pages/HomeScreen';
import Login from './pages/Login';
import Signup from './pages/Signup';
import FormComponent from './components/form/FormComponent';
import Journal from './pages/JournalTwo';
import Landing from './components/LandingNew';
import UserProfile from './pages/UserProfile';
import Quests from './pages/Quests';
import ManageValues from './components/form/ManageValues';
import Onboarding from './components/onboarding/Onboarding';
import EntryPage from './components/entry/EntryPage';
import ExplanationScreen from './components/questCreator/ExplanationScreen';
import RecoverPassword from './components/profile/RecoverPassword';
import AuthTabs from './pages/AuthTabs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import QuestDetails from './components/quest/QuestDetails';
import UpgradeSuccess from './pages/UpgradeSuccess';
import TermsOfService from './pages/TermsOfService';
import About from './pages/About';
import ContactUs from './pages/ContactUs';
import Match from './pages/Match';
import Explore from './pages/Explore';
import SharableDashboard from './components/share/SharableDashboard';
import Connect from './pages/Connect';
import DashboardView from './pages/DashboardView';
import FinalizeLogin from './pages/FinalizeLogin';
import FinalizeSignup from './pages/FinalizeSignup';

const AuthRedirect = () => {
  const { user } = useAuthContext();
  return user ? <Navigate to="/home" replace /> : <Landing />;
};

const Layout = () => {
  const { user } = useAuthContext();

  return (
    <div className="App">
      {user && <Navbar />}
      <main>
        <Outlet />
      </main>
      {user && <MobileNav />}
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, authIsReady } = useAuthContext();

  if (!authIsReady) {
    return null; // Let the loading state be handled at the app level
  }

  return user ? children : <Navigate to="/login" replace />;
};

function App() {
  const { authIsReady } = useAuthContext();

  const router = createBrowserRouter([
    {
      path: '/',
      element: <Layout />,
      children: [
        { index: true, element: <AuthRedirect /> },
        { path: 'privacy-policy', element: <PrivacyPolicy /> },
        { path: 'terms-of-service', element: <TermsOfService /> },
        { path: 'about', element: <About /> },
        { path: 'contact', element: <ContactUs /> },
        { path: 'recover-password', element: <RecoverPassword /> },
        { path: 'onboarding', element: <Onboarding /> },
        { path: 'signup', element: <AuthTabs /> },
        { path: 'login', element: <AuthTabs /> },
        { path: 'finalize-login', element: <FinalizeLogin /> },
        { path: 'finalize-signup', element: <FinalizeSignup /> },
        // Protected routes
        { path: 'home', element: <ProtectedRoute><HomeScreen /></ProtectedRoute> },
        { path: 'journal', element: <ProtectedRoute><Journal /></ProtectedRoute> },
        { path: 'match', element: <ProtectedRoute><Match /></ProtectedRoute> },
        { path: 'explore', element: <ProtectedRoute><Explore /></ProtectedRoute> },
        { path: 'quests', element: <ProtectedRoute><Quests /></ProtectedRoute> },
        { path: 'new-entry', element: <ProtectedRoute><FormComponent /></ProtectedRoute> },
        { path: 'edit-entry/:entryId', element: <ProtectedRoute><FormComponent /></ProtectedRoute> },
        { path: 'entry/:entryId', element: <ProtectedRoute><EntryPage /></ProtectedRoute> },
        { path: 'profile', element: <ProtectedRoute><UserProfile /></ProtectedRoute> },
        { path: 'manage-values/:type', element: <ProtectedRoute><ManageValues /></ProtectedRoute> },
        { path: 'quest/:questId', element: <ProtectedRoute><QuestDetails /></ProtectedRoute> },
        { path: 'create-quest', element: <ProtectedRoute><ExplanationScreen /></ProtectedRoute> },
        { path: 'share/:userId', element: <ProtectedRoute><SharableDashboard /></ProtectedRoute> },
        { path: 'connect', element: <ProtectedRoute><Connect /></ProtectedRoute> },
        { path: 'dashboards/:userId', element: <ProtectedRoute><DashboardView /></ProtectedRoute> },
        { path: 'upgrade-success', element: <ProtectedRoute><UpgradeSuccess /></ProtectedRoute> },
        { path: '*', element: <div>404 Not Found</div> }
      ]
    }
  ]);

  if (!authIsReady) {
    return (
      <div className="App">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return <RouterProvider router={router} />;
}

export default App;